/**
 * Change a subscription onto a retail offer the server will accept (TMMPDM-0008).
 * TMMPDM-0032 (tmm-api 0101): a refusal (409 `RATE_CARD_NOT_ACTIVE`; TMMAPI-0154 retired the non-compliant one)
 * says "Activate or fix the card first."
 * TMMPDM-0048 (tmm-api 0111): the picker lists only `selectable` offers. The others are named
 * below it, with the server's reason.
 * TMMPDM-0067: the server's `selectable` alone determines the picker, including closed offers.
 */
import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { ErrorState, SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import type { OfferNotSelectableReason, OfferView } from '@/api/tmm/types';
import { ApiError, errorCopy } from '@/api/errors';
import { notPricedCode, TenantNotPricedAlert, type NotPricedCode } from './TenantNotPricedAlert';

interface ChangeOfferDialogProps {
  open: boolean;
  slug: string;
  subscriptionId: number;
  resellerId: number | null;
  resellerName?: string | null | undefined;
  /** The tenant's currency, for TENANT_CURRENCY_NOT_PRICED (TMMPDM-0025). */
  currency?: string | undefined;
  onClose: () => void;
  onDone: () => void;
}

export function ChangeOfferDialog({
  open,
  onClose,
  slug,
  subscriptionId,
  resellerId,
  resellerName,
  currency,
  onDone,
}: ChangeOfferDialogProps) {
  // The form stays mounted through the close. MUI 9 Dialog has no TransitionProps;
  // the transition slot's onExited is that event. A key bump there is after the
  // paper is gone, so the name "Change offer" lasts until MUI removes the dialog.
  const [formKey, setFormKey] = useState(0);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        transition: { onExited: () => setFormKey((key) => key + 1) },
      }}
    >
      <ChangeOfferForm
        key={`${slug}:${formKey}`}
        slug={slug}
        subscriptionId={subscriptionId}
        resellerId={resellerId}
        resellerName={resellerName}
        currency={currency}
        onClose={onClose}
        onDone={onDone}
      />
    </Dialog>
  );
}

function ChangeOfferForm({
  slug,
  subscriptionId,
  resellerId,
  resellerName,
  currency,
  onClose,
  onDone,
}: Omit<ChangeOfferDialogProps, 'open'>) {
  const { t } = useCopy();
  const reasonsId = useId();
  const [offers, setOffers] = useState<OfferView[]>([]);
  const [offersLoaded, setOffersLoaded] = useState(false);
  const [offersFailure, setOffersFailure] = useState<{ cause: unknown } | null>(null);
  const [offerAttempt, setOfferAttempt] = useState(0);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [offerId, setOfferId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notPriced, setNotPriced] = useState<NotPricedCode | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listOffers(slug)
      .then((list) => {
        if (cancelled) return;
        setOffers(list.offers);
        setOffersFailure(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setOffers([]);
        setOffersFailure({ cause: err });
      })
      .finally(() => {
        if (!cancelled) setOffersLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, offerAttempt]);

  const choosable = offers.filter((offer) => offer.selectable);
  const blocked = offers.filter((offer) => !offer.selectable);

  const reasonSentence = (name: string, reason: OfferNotSelectableReason | null): string | null => {
    if (reason === 'OFFER_CLOSED') {
      return t('subscriptions.changeOffer.closed', '{{name}}: this offer version is closed.', {
        name,
      });
    }
    if (reason === 'RATE_CARD_NOT_ACTIVE') {
      return t(
        'subscriptions.changeOffer.reasonNotActive',
        "{{name}}: its rate card isn't active.",
        { name },
      );
    }
    return null;
  };

  const submit = async () => {
    if (offerId === '') return;
    setSubmitting(true);
    setError(null);
    setNotPriced(null);
    try {
      await adminApi.changeOffer(slug, subscriptionId, { retailOfferId: offerId });
      onDone();
    } catch (err: unknown) {
      const notPricedAs = notPricedCode(err);
      const code = err instanceof ApiError ? String(err.code) : '';
      if (notPricedAs) {
        setNotPriced(notPricedAs);
      } else if (code === 'OFFER_NOT_ACTIVE') {
        setError(
          t(
            'subscriptions.changeOffer.offerInactive',
            'That offer is no longer active. Choose another.',
          ),
        );
        setOfferId('');
        setOffersLoaded(false);
        setOfferAttempt((n) => n + 1);
      } else if (code === 'RATE_CARD_NOT_ACTIVE') {
        setError(t('tenantRateCard.activateOrFix', 'Activate or fix the card first.'));
      } else {
        setError(
          errorCopy(err, t, t('subscriptions.changeOffer.error', "Couldn't change the offer.")),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('subscriptions.changeOffer.title', 'Change offer')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {notPriced ? (
            <TenantNotPricedAlert
              code={notPriced}
              resellerId={resellerId}
              resellerName={resellerName}
              currency={currency}
            />
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!offersLoaded ? (
            <Typography variant="body2" color="text.secondary">
              {t('common.loading', 'Loading')}
            </Typography>
          ) : offersFailure ? (
            <ErrorState
              error={offersFailure.cause}
              message={t('subscriptions.changeOffer.loadError', 'Could not load offers.')}
              onRetry={() => {
                setOffersLoaded(false);
                setOffersFailure(null);
                setOfferAttempt((n) => n + 1);
              }}
            />
          ) : choosable.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('subscriptions.changeOffer.none', 'No offer can be chosen right now.')}
            </Typography>
          ) : (
            <TextField
              select
              label={t('subscriptions.changeOffer.offer', 'Offer')}
              value={offerId === '' ? '' : String(offerId)}
              onChange={(event) => setOfferId(Number(event.target.value))}
            >
              {choosable.map((offer) => (
                <MenuItem key={offer.id} value={String(offer.id)}>
                  {offer.current
                    ? t('subscriptions.offerCurrent', 'current')
                    : t('subscriptions.changeOffer.offerN', 'Offer {{id}}', { id: offer.id })}
                </MenuItem>
              ))}
            </TextField>
          )}
          {blocked.length > 0 ? (
            <Box>
              <Button
                type="button"
                size="small"
                aria-expanded={reasonsOpen}
                aria-controls={reasonsId}
                onClick={() => setReasonsOpen((openNow) => !openNow)}
                sx={{ px: 0 }}
              >
                {t('subscriptions.changeOffer.unselectable', "{{count}} offers can't be chosen", {
                  count: blocked.length,
                })}
              </Button>
              <Box component="ul" id={reasonsId} hidden={!reasonsOpen} sx={{ m: 0, pl: 2.5 }}>
                {blocked.map((offer) => {
                  const name = t('subscriptions.changeOffer.offerN', 'Offer {{id}}', {
                    id: offer.id,
                  });
                  const sentences: string[] = [];
                  const reason = reasonSentence(name, offer.notSelectableReason);
                  if (reason) sentences.push(reason);
                  if (sentences.length === 0) {
                    sentences.push(
                      t(
                        'subscriptions.changeOffer.unexplained',
                        "{{name}} can't be chosen right now.",
                        { name },
                      ),
                    );
                  }
                  const text = sentences.join(' ');
                  return (
                    <Typography key={offer.id} component="li" variant="body2">
                      {text}
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('subscriptions.subscribeDialog.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting} disabled={offerId === ''}>
          {t('subscriptions.changeOffer.submit', 'Change')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
